import { LoginWorkflowLayout } from "components/app/login-workflow-layout";
import { SupportEmailLink } from "components/app/support-email-link";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import * as React from "react";
import { connect } from "react-redux";
import { Action, Dispatch } from "redux";
import { clearPasswordReset2FA, resetPassword } from "store/actions/password-reset";
import { ApplicationState } from "store/state";
import { IResetPasswordState } from "../../store/reducers/reset-password";

interface ResetPasswordPropsFromDispatch {
    doResetPassword: (email: string) => void;
    clearPasswordReset2FA: () => void;
}

interface ResetPasswordPropsFromState {
    resetPassword: IResetPasswordState;
}

interface ResetPasswordState {
    email: string;
    isValidEmail: boolean;
    formIsSubmitted: boolean;
}

interface LoginPropsFromDispatch {
    doResetPassword: (email: string) => void;
    clearPasswordReset2FA: () => void;
}

type ResetPasswordProps = ResetPasswordPropsFromState & ResetPasswordPropsFromDispatch;

class ResetPassword extends React.Component<ResetPasswordProps, ResetPasswordState> {
    constructor(props: ResetPasswordProps) {
        super(props);
        this.state = {
            email: "",
            isValidEmail: false,
            formIsSubmitted: false,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onSubmit(e: any) {
        e.preventDefault();
        this.setState({ ...this.state, formIsSubmitted: true });

        if (!this.state.isValidEmail) {
            return;
        }

        this.props.doResetPassword(this.state.email);
    }

    onEmailChange(e: React.ChangeEvent<HTMLInputElement>) {
        const email = e.target.value;
        this.setState({ ...this.state, email, isValidEmail: this.hasValidFormat(email) });
    }

    hasValidFormat(email: string): boolean {
        const emailRegex =
            /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
        return emailRegex.test(email);
    }

    onBackToEmailForm = () => {
        this.props.clearPasswordReset2FA();
    };

    override render() {
        const { isSuccessful, isFailed } = this.props.resetPassword;
        return (
            <LoginWorkflowLayout title="Reset your password">
                <div className="space-y-6">
                    <p className="text-sm text-gray-600">
                        Enter your email address and we&apos;ll send you a link to reset
                        your password.
                    </p>

                    {isFailed && (
                        <InlineBanner type="error">
                            Something went wrong. Please contact <SupportEmailLink /> for
                            further information.
                        </InlineBanner>
                    )}

                    {isSuccessful && (
                        <InlineBanner type="success">
                            Please check your email for instructions on resetting your
                            password
                        </InlineBanner>
                    )}

                    <form
                        onSubmit={this.onSubmit.bind(this)}
                        noValidate
                        className="space-y-6">
                        <Input
                            type="email"
                            value={this.state.email}
                            onChange={this.onEmailChange.bind(this)}
                            label="Email address"
                        />

                        {!this.state.isValidEmail && this.state.formIsSubmitted && (
                            <InlineBanner type="error">
                                Please enter a valid email address
                            </InlineBanner>
                        )}

                        <PrimaryButton type="submit" className="w-full">
                            Reset Password
                        </PrimaryButton>
                    </form>
                </div>
            </LoginWorkflowLayout>
        );
    }
}

function mapStateToProps(state: ApplicationState): ResetPasswordPropsFromState {
    return {
        resetPassword: state.resetPassword,
    };
}

function mapDispatchToProps(dispatch: Dispatch<Action>): LoginPropsFromDispatch {
    return {
        doResetPassword: (email: string) => resetPassword(email, dispatch),
        clearPasswordReset2FA: () => clearPasswordReset2FA(dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
